import { useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"

import { Action } from "history"
import { PRODUCT } from "shared/constants/permalinks"
import { last } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"

import { getProductType } from "entities/products/lib/getProductType"
import {
  ScrollPosition,
  getLastHistoryAction,
  getScrollPosition as getScrollPositionFromStore,
  saveScrollPosition as saveScrollPositionToStore
} from "features/scroll-management/model/scroll-position-store"

export function getScrollRelevantPath(pathname: string) {
  if (pathname.includes(`${PRODUCT}`)) {
    const pathComponents = pathname.split("/")
    const productType = getProductType(last(pathComponents))
    pathComponents.splice(-1, 1, productType)
    return pathComponents.join("/")
  }
  return pathname
}

export interface ScrollControllerOptions {
  restoreOnPush?: boolean
}

export function useScrollController(options?: ScrollControllerOptions) {
  const { pathname } = useLocation()

  const scrollRelevantPath = useMemo(() => {
    return getScrollRelevantPath(pathname)
  }, [pathname])

  const saveScrollPosition = useEvent(() => {
    saveScrollPositionToStore(scrollRelevantPath, {
      left: Math.floor(window.scrollX),
      top: Math.floor(window.scrollY)
    })
  })

  useEffect(() => {
    const shouldRestore =
      getLastHistoryAction() === Action.Push ? !!options?.restoreOnPush : true
    const scrollPosition: ScrollPosition = shouldRestore
      ? getScrollPositionFromStore(scrollRelevantPath)
      : { top: 0, left: 0 }
    window.scrollTo({
      ...scrollPosition,
      behavior: "instant" as ScrollBehavior
    })
  }, [scrollRelevantPath])

  useEffect(() => {
    window.addEventListener("scroll", saveScrollPosition)
    return () => window.removeEventListener("scroll", saveScrollPosition)
  }, [])
}
